import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AumicoAbcpConfig, AumicoAbcpConfigService } from '../abcp-data.config';

/** One entry as `aumico-be` returns it. i18n KEYS, never prose — the caller resolves them. */
export interface IClosingActivityEntry {
    id: string;
    actorType: 'agent' | 'user';
    actorName: string | null;
    titleKey: string;
    detailKey: string | null;
    params: Record<string, string | number>;
    /** ISO-8601 with offset. The host formats; the backend never sends a label. */
    at: string;
    category: string;
    /** `null` = an entry belonging to the closing rather than to one account. */
    accountId: string | null;
}

export interface IClosingActivityPage {
    entries: IClosingActivityEntry[];
    /** This half's WHOLE count in the current filter — not the page size, and not a total over both halves. */
    total: number;
    /** Opaque; hand it back untouched. `null` = this half is exhausted. */
    nextCursor: string | null;
}

/**
 * HTTP for the HUMAN half of the ABCP activity log — REV-21378, `ClosingActivityController`.
 *
 * ⚠️ **This is one half, deliberately.** The agent half comes from `ms-ai-agents` through
 * `AiAgentHttpService`, and nginx routes `/api/ai/**` there directly (ADR-0018 §6) — so there is no
 * service-to-service hop and nothing server-side can see both. The CLIENT merges them
 * (`activity-merge.ts`). A failure here is therefore an ERROR, not a partial page: only the caller
 * knows whether the other half arrived.
 *
 * ⚠️ **`accountId` is required.** The surface is account-only (CPO decision D1); there is no
 * closing-wide variant to ask for.
 */
@Injectable()
export class ClosingActivityService {
    private readonly baseUrl: string;

    public constructor(
        private readonly http: HttpClient,
        @Inject(AumicoAbcpConfigService) config: AumicoAbcpConfig,
    ) {
        // NO leading slash: `apiGateway` already ENDS with one, so `${apiGateway}/projects` yields
        // `/api//projects` and the gateway 404s. Same concatenation as `closing-document.service.ts`.
        this.baseUrl = `${config.apiGateway}projects`;
    }

    /**
     * One page of this account's human-side entries, newest first.
     *
     * `cursor` is the previous page's `nextCursor` and belongs to THIS half only — handing the agent
     * half's position here would make one source skip entries it never delivered.
     */
    public getActivity$(projectId: string, accountId: string, cursor: string | null, limit?: number): Observable<IClosingActivityPage> {
        let params = new HttpParams().set('accountId', accountId);
        if (cursor !== null) {
            params = params.set('cursor', cursor);
        }
        if (limit !== undefined) {
            params = params.set('limit', String(limit));
        }

        return this.http.get<IClosingActivityPage>(`${this.baseUrl}/${projectId}/activity`, { params });
    }
}
